import React from 'react';
import { Box, Typography, Grid, Card, useTheme } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Link } from 'react-router-dom';
import { playClickSound } from '../../utils/soundEffects';

const PORTAL_DESTINATIONS = [
  {
    id: 'codeforces',
    title: 'Codeforces Live Arena',
    subtitle: 'Max Rating: 2013 (Candidate Master)',
    desc: 'Live contest performance telemetry, interactive rating trajectory chart, and problem tags compendium.',
    icon: <CodeIcon sx={{ fontSize: 28, color: '#34d399' }} />,
    color: '#34d399',
    badge: 'LIVE TELEMETRY',
    link: '/codeforces',
    xs: 12,
    md: 6,
    bgGradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.08) 0%, rgba(13, 17, 26, 0.95) 100%)',
    border: 'rgba(52, 211, 153, 0.35)'
  },
  {
    id: 'bookshelf',
    title: '3D Luxury Bookshelf',
    subtitle: 'Physical Hardcover Folio Inspector',
    desc: 'Tactile 3D books with authentic leather bindings, gold filigree frames, ribbon bookmarks, and marginalia notes.',
    icon: <AutoStoriesIcon sx={{ fontSize: 28, color: '#fbbf24' }} />,
    color: '#fbbf24',
    badge: '3D INTERACTIVE',
    link: '/reading/bookshelf',
    xs: 12,
    md: 6,
    bgGradient: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(13, 17, 26, 0.95) 100%)',
    border: 'rgba(251, 191, 36, 0.35)'
  },
  {
    id: 'constellation',
    title: '2D Knowledge Constellation',
    subtitle: 'Interdisciplinary Synthesis Graph',
    desc: 'Physics-based orbital node graph interconnecting Systems Programming, Graph Theory, Statecraft, and Evolutionary Biology.',
    icon: <AccountTreeIcon sx={{ fontSize: 28, color: '#38bdf8' }} />,
    color: '#38bdf8',
    badge: 'PHYSICS GRAPH',
    link: '/learning',
    xs: 12,
    md: 4,
    bgGradient: 'linear-gradient(135deg, rgba(56, 189, 248, 0.08) 0%, rgba(13, 17, 26, 0.95) 100%)',
    border: 'rgba(56, 189, 248, 0.35)'
  },
  {
    id: 'history-scrubber',
    title: 'Civilizational Epochs',
    subtitle: 'Human Thought & Computing Parallels',
    desc: 'Interactive chronological scrubber mapping 6 major epochs from Ancient Magadha & Rome to Modern Distributed Cloud Systems.',
    icon: <HistoryEduIcon sx={{ fontSize: 28, color: '#c084fc' }} />,
    color: '#c084fc',
    badge: 'CHRONOLOGY',
    link: '/history',
    xs: 12,
    md: 4,
    bgGradient: 'linear-gradient(135deg, rgba(192, 132, 252, 0.08) 0%, rgba(13, 17, 26, 0.95) 100%)',
    border: 'rgba(192, 132, 252, 0.35)'
  },
  {
    id: 'resume-dossier',
    title: 'Ultra-Colorful Resume',
    subtitle: 'Widescreen + 1-Page LaTeX Print',
    desc: 'Multi-chromatic typography, neon category left-accent cards, full responsive scaling, and single-click PDF generator.',
    icon: <DescriptionIcon sx={{ fontSize: 28, color: '#f472b6' }} />,
    color: '#f472b6',
    badge: 'EXECUTIVE CV',
    link: '/resume',
    xs: 12,
    md: 4,
    bgGradient: 'linear-gradient(135deg, rgba(244, 114, 182, 0.08) 0%, rgba(13, 17, 26, 0.95) 100%)',
    border: 'rgba(244, 114, 182, 0.35)'
  }
];

const PortalTeleportMatrix = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ mb: 7 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: isDark ? '#e5a93c' : '#b45309', fontSize: 22 }} />
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.8rem',
            fontWeight: 800,
            color: isDark ? '#e5a93c' : '#b45309',
            letterSpacing: '0.1em',
            textTransform: 'uppercase'
          }}
        >
          CURATED PORTALS & EXPERIENCES //
        </Typography>
      </Box>

      <Typography
        variant="h2"
        sx={{
          fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
          fontSize: { xs: '1.6rem', sm: '2.1rem' },
          fontWeight: 700,
          mb: 3,
          color: theme.palette.text.primary
        }}
      >
        Flagship Portals & Interactive Systems
      </Typography>

      {/* Bento Grid */}
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {PORTAL_DESTINATIONS.map((portal) => (
          <Grid item xs={portal.xs} md={portal.md} key={portal.id}>
            <Card
              component={Link}
              to={portal.link}
              onClick={playClickSound}
              sx={{
                p: { xs: 2.5, sm: 3.2 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                textDecoration: 'none',
                borderRadius: '20px',
                background: isDark
                  ? portal.bgGradient
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 245, 238, 0.98) 100%)',
                border: `1.5px solid ${isDark ? portal.border : 'rgba(0,0,0,0.1)'}`,
                borderTop: `4px solid ${portal.color}`,
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 6px 20px rgba(0,0,0,0.06)',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: portal.color,
                  boxShadow: `0 16px 40px ${portal.border}, 0 0 25px ${portal.color}22`
                }
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.8, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {portal.icon}
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        backgroundColor: `${portal.color}22`,
                        color: portal.color,
                        px: 1,
                        py: 0.3,
                        borderRadius: '6px',
                        border: `1px solid ${portal.color}44`
                      }}
                    >
                      {portal.badge}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.4rem' },
                    mb: 0.4,
                    color: isDark ? '#f8fafc' : '#0f172a'
                  }}
                >
                  {portal.title}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: portal.color,
                    mb: 1.4
                  }}
                >
                  {portal.subtitle}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontSize: '0.86rem',
                    lineHeight: 1.62,
                    mb: 2
                  }}
                >
                  {portal.desc}
                </Typography>
              </Box>

              <Box
                sx={{
                  pt: 1.5,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: portal.color,
                  fontWeight: 700,
                  fontSize: '0.82rem'
                }}
              >
                <span>Launch Interactive Portal</span>
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PortalTeleportMatrix;
